"use client";

import { Home } from "lucide-react";
import Link from "next/link";
import { useEffect } from "react";

import { ThemeProvider } from "@/app/providers/ThemeProvider";
import { RandomFoodIcon } from "@/components/shared/RandomFoodIcon";
import { Button } from "@/components/ui/button";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

/**
 * Root error boundary — sits outside [locale]/layout.tsx so it has no
 * NextIntlClientProvider context. Uses hardcoded English strings as a
 * last-resort fallback. Locale-aware errors are handled by
 * app/[locale]/error.tsx instead.
 */
export default function Error({ error, reset }: ErrorProps) {
  useEffect(() => {
    console.error("Error:", error);
  }, [error]);

  return (
    <ThemeProvider>
      <div className="min-h-dvh bg-background flex items-center justify-center px-3">
        <div className="flex flex-col items-center justify-center gap-6 max-w-md text-center">
          <RandomFoodIcon categories={["produce"]} />

          <div className="space-y-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground">
              Something went wrong
            </h2>
          </div>

          <p className="text-base sm:text-lg text-text-secondary max-w-sm">
            An unexpected error occurred. Please try again.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mt-2">
            <Button onClick={reset} variant="outline" size="lg" className="gap-2">
              Try Again
            </Button>
            <Link href="/">
              <Button size="lg" className="gap-2">
                <Home className="size-5" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
}
